import { Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import { useIOSPortrait } from "@/hooks/useIOSPortrait";

interface ProductImageDisplayProps {
  imageUrl: string;
  alt: string;
  className?: string;
  size?: "sm" | "md" | "lg";
  showBadge?: boolean;
  badgeText?: string;
  variant?: "default" | "minimal" | "elegant";
  ribbonText?: string;
  onClick?: () => void;
}

export function ProductImageDisplay({
  imageUrl,
  alt,
  className,
  size = "md",
  showBadge = false,
  badgeText,
  variant = "default",
  ribbonText,
  onClick,
}: ProductImageDisplayProps) {
  const isIOSPortrait = useIOSPortrait();

  const sizeClasses = {
    sm: "w-full",
    md: "w-full",
    lg: "w-full",
  };

  return (
    <div className={cn("relative group", sizeClasses[size], className)}>
      {/* Outer gradient border frame - FIXED: disable transition on iOS */}
      <div
        className={cn(
          "absolute inset-0 bg-gradient-to-br from-primary via-rose to-blush rounded-2xl opacity-80",
          !isIOSPortrait && "group-hover:opacity-100 transition-opacity duration-300",
        )}
      />

      {/* Inner background (creates the border effect) */}
      <div className="absolute inset-[3px] bg-card rounded-[13px]" />

      {/* Gold corner accents - top left - FIXED: disable transitions on iOS */}
      <div
        className={cn(
          "absolute top-1 left-1 w-6 h-6 sm:w-8 sm:h-8 border-l-2 border-t-2 border-gold/70 rounded-tl-xl",
          !isIOSPortrait && "transition-all duration-300 group-hover:w-10 group-hover:h-10 group-hover:border-gold",
        )}
      />

      {/* Gold corner accents - bottom right - FIXED: disable transitions on iOS */}
      <div
        className={cn(
          "absolute bottom-1 right-1 w-6 h-6 sm:w-8 sm:h-8 border-r-2 border-b-2 border-gold/70 rounded-br-xl",
          !isIOSPortrait && "transition-all duration-300 group-hover:w-10 group-hover:h-10 group-hover:border-gold",
        )}
      />

      {/* Sparkle accent on border - FIXED: already disabled animation on iOS portrait */}
      <div
        className={cn(
          "absolute -top-1.5 -right-1.5 sm:-top-2 sm:-right-2 z-20 bg-gradient-rose rounded-full p-1.5 sm:p-2 shadow-glow",
          !isIOSPortrait && "animate-sparkle-pulse",
        )}
      >
        <Sparkles className="h-3 w-3 sm:h-4 sm:w-4 text-primary-foreground" />
      </div>

      {/* Ribbon badge - FIXED: remove transform on iOS */}
      {ribbonText && (
        <div
          className={cn(
            "absolute top-4 -left-2 z-20 bg-gradient-rose text-primary-foreground text-xs font-semibold px-3 py-1 rounded-r-full shadow-md",
            !isIOSPortrait && "transform -rotate-2",
          )}
        >
          {ribbonText}
        </div>
      )}

      {/* Main product image container */}
      <div className="relative z-10 m-[3px] rounded-[13px] overflow-hidden">
        <img
          src={imageUrl}
          alt={alt}
          width={300}
          height={300}
          loading="lazy"
          decoding="async"
          onClick={onClick}
          className="w-full h-full object-cover cursor-pointer touch-manipulation select-none"
          style={{
            // CRITICAL: Explicit dimensions prevent layout shift on iOS
            aspectRatio: "1 / 1",
            maxWidth: "100%",
            height: "auto",
            // CRITICAL: Force GPU acceleration to prevent zoom
            transform: "translateZ(0)",
            WebkitTransform: "translateZ(0)",
            backfaceVisibility: "hidden",
            WebkitBackfaceVisibility: "hidden",
            // CRITICAL: Isolate from parent transforms
            willChange: "auto",
            contain: "layout",
          }}
        />

        {/* Inner pink glow overlay - FIXED: disable transition on iOS */}
        <div
          className={cn(
            "absolute inset-0 pointer-events-none",
            isIOSPortrait
              ? "shadow-[inset_0_0_30px_rgba(232,170,185,0.25)]"
              : "shadow-[inset_0_0_30px_rgba(232,170,185,0.25)] group-hover:shadow-[inset_0_0_40px_rgba(232,170,185,0.35)] transition-all duration-300",
          )}
        />

        {/* Subtle gradient overlay on hover - FIXED: disable on iOS */}
        {!isIOSPortrait && (
          <div className="absolute inset-0 bg-gradient-to-t from-primary/10 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
        )}
      </div>

      {/* Optional badge */}
      {showBadge && badgeText && (
        <div className="absolute bottom-4 left-4 z-20 px-3 py-1.5 bg-card/95 backdrop-blur-sm rounded-full text-sm font-medium text-foreground shadow-sm border border-primary/20">
          {badgeText}
        </div>
      )}
    </div>
  );
}
